
import { DirectionResponse } from '@clients/mapbox';
import { DirectedRoute, GetTrip, Location, RuleTriggerEnum, RuleV2, Trip, TripMutation, Zone } from '@clients/myTrips';
import deepCopy, { deepEquals } from '@util/copy';
import { LoadState } from '@util/types';
import { findClosestZoneContainingLocation } from '@util/zoneService';
import { Component, Prop, Watch } from 'vue-property-decorator';
import Vue from 'vue';
import TripDetailDataLine from '@components/trip-detail/trip-data/TripDetailDataLine.vue';
import IconStart from '@components/icons/trip-details/icon-start.svg';
import IconEnd from '@components/icons/trip-details/icon-end.svg';
import IconAdd from '@components/icons/icon-add.svg';
import AppButton from '@components/new-styleguide/AppButton.vue';
import TagSelect from '@components/new-styleguide/form/TagSelect.vue';
import { namespace } from 'vuex-class';
import PilotSelect from '@components/new-styleguide/form/PilotSelect.vue';
import TripDataWorkNote from '@components/trip-detail/trip-data/TripDataWorkNote.vue';
import TripDataPilotTag from '@components/trip-detail/trip-data/TripDataPilotTag.vue';
import TripDataEnd from '@components/trip-detail/trip-data/TripDataEnd.vue';
import TripDataStops from '@components/trip-detail/trip-data/TripDataStops.vue';
import TripDataStart from '@components/trip-detail/trip-data/TripDataStart.vue';

const SettingsModule = namespace('Settings');
const GeographicsModule = namespace('Geographics');

const maxSpeed = 200 // km/h
const minSpeed = 0.5 // km/h

const maxDuration = 24 * 60 // 1 day in minutes
const minDuration = 1 // minute

interface DirectionData {
  distance: number,  // metres
  duration: number,  // seconds
  routeGeometry: string
}

interface RuleWithZones extends RuleV2 {
  zones: Zone[]
}

@Component({
  name: 'TripDetailTabData',
  components: {
    TripDataStart,
    TripDataStops,
    TripDataEnd,
    TripDataPilotTag,
    TripDataWorkNote,
    PilotSelect,
    TagSelect,
    AppButton,
    TripDetailDataLine,
    IconStart,
    IconEnd,
    IconAdd
  }
})
export default class TripDetailTabData extends Vue {
  $refs!: {
    start: TripDataStart,
    stops: TripDataStops,
    end: TripDataEnd,
    worknote: TripDataWorkNote
  }

  @Prop({ default: false })
  private addMode: boolean;

  @Prop()
  private trip: GetTrip

  @Prop()
  private tripMutation: TripMutation;

  @Prop()
  private prevTrip: Trip;

  @Prop()
  private nextTrip: Trip;

  @Prop()
  private addType: string;

  @Prop({ default: false })
  private loading: boolean;

  private latestDirectionData: DirectionData = null;

  private shiftEndOdometerBackwards: boolean = false;

  @GeographicsModule.Action('fetchDirection') fetchDirection;

  @GeographicsModule.Action('fetchDirectedRoutes') fetchDirectedRoutes;

  @GeographicsModule.Getter('getDirectedRoutesResponse') directedRoutesResponse: DirectedRoute[];

  @GeographicsModule.Getter('getDirectedRoutesStatus') directedRoutesStatus: LoadState;

  @SettingsModule.Getter('getZones') zones: Zone[];

  @SettingsModule.Getter('getRulesV2') rules: RuleV2[];

  created() {
    this.fetchDirectedRoutes();
  }

  @Watch('loading')
  private onLoadingStateChange() {
    if (!this.loading) {
      if (!this.addMode) {
        this.latestDirectionData = {
          distance: this.distance,
          duration: this.duration * 60,
          routeGeometry: this.trip.routeGeometry
        }
      }
    }
  }

  async mounted() {
    this.updateNewTripMutation();
  }

  @Watch('addType')
  private onAddTypeChange() {
    if (this.addMode && this.addType === 'last' && this.isNewLastTrip) {
      this.shiftEndOdometerBackwards = true;
      const copy = deepCopy(this.tripMutation);
      this.handleNewTripCurrentDateTimeAdjustment(copy)
      this.$emit('input', copy);
    }
  }

  get editMode() {
    return !!this.tripMutation;
  }

  get minOdometer() {
    if (this.prevTrip && this.prevTrip.endInfo && this.prevTrip.endInfo.odometer) {
      return this.prevTrip.endInfo.odometer;
    }
    return 0;
  }

  get maxOdometer() {
    if (this.nextTrip && this.nextTrip.startInfo && this.nextTrip.startInfo.odometer) {
      return this.nextTrip.startInfo.odometer;
    }
  }

  get minTime() {
    return (this.prevTrip && this.prevTrip.endInfo && this.prevTrip.endInfo.timestamp) ? this.prevTrip.endInfo.timestamp : null;
  }

  get maxTime() {
    return (this.nextTrip && this.nextTrip.startInfo && this.nextTrip.startInfo.timestamp) ? this.nextTrip.startInfo.timestamp : new Date().toISOString();
  }

  get distance() {
    if (this.editMode) {
      if (!this.tripMutation.endInfo || !this.tripMutation.startInfo) {
        return 0;
      }
      return Math.max(0, this.tripMutation.endInfo.odometer - this.tripMutation.startInfo.odometer);
    } else {
      if (!this.trip.endInfo || !this.trip.startInfo) {
        return 0;
      }
      return Math.max(0, this.trip.endInfo.odometer - this.trip.startInfo.odometer);
    }
  }

  get duration() {
    if (this.editMode) {
      if (this.tripMutation.endInfo && this.tripMutation.endInfo.timestamp && this.tripMutation.startInfo.timestamp) {
        return (new Date(this.tripMutation.endInfo.timestamp).getTime() - new Date(this.tripMutation.startInfo.timestamp).getTime()) / 60000;
      }
    } else {
      if (this.trip.endInfo && this.trip.endInfo.timestamp && this.trip.startInfo.timestamp) {
        return (new Date(this.trip.endInfo.timestamp).getTime() - new Date(this.trip.startInfo.timestamp).getTime()) / 60000;
      }
    }
    return 0;
  }

  get rulesWithZones(): RuleWithZones[] {
    return this.rules
      .filter(r => r.vehicleIds.includes(this.tripMutation.vehicleId))
      .map(r => ({
        ...r,
        zones: this.zones.filter(z => r.zoneIds.includes(z.id))
      }))
      .filter(r => r.zones)
  }

  get startRules() {
    return this.rulesWithZones
      .filter(r => r.triggers.includes(RuleTriggerEnum.TripStart))
  }

  get endRules() {
    return this.rulesWithZones
      .filter(r => r.triggers.includes(RuleTriggerEnum.TripEnd))
  }

  get isNewLastTrip() {
    return this.addMode && this.tripMutation && this.tripMutation.endInfo.timestamp && !this.tripMutation.endInfo.address && this.tripMutation.startInfo.timestamp;
  }

  get isStartInfoPrefilled() {
    return !!this.prevTrip && this.addMode;
  }

  get isEndInfoPrefilled() {
    return !!this.nextTrip && this.addMode;
  }

  get isFirstTrip() {
    return !this.isStartInfoPrefilled && !this.isEndInfoPrefilled && this.addMode;
  }

  get isFillingGap() {
    return this.isStartInfoPrefilled && this.isEndInfoPrefilled && this.addMode;
  }

  public async updateNewTripMutation() {
    if (this.isNewLastTrip) {
      this.shiftEndOdometerBackwards = true;
      const copy = deepCopy(this.tripMutation);
      this.handleNewTripCurrentDateTimeAdjustment(copy)
      this.$emit('input', copy);
    } else if (this.addMode && this.tripMutation && this.tripMutation.startInfo.address && this.tripMutation.endInfo.address) {
      const copy = deepCopy(this.tripMutation);
      await this.fetchAndUpdateDirection(copy)
      this.$emit('input', copy);
    } else if (this.addMode && this.tripMutation && !(this.tripMutation.startInfo.address && this.tripMutation.endInfo.address)) {
      const copy = deepCopy(this.tripMutation);
      this.handleStartInfoValueChanged(copy)
      this.$emit('input', copy);
    }
  }

  private handleInputStart(copy: TripMutation) {
    this.handleStartInfoValueChanged(copy);
  }

  private handleInputEnd(copy: TripMutation) {
    this.handleEndInfoValueChanged(copy);
  }

  private handleInput(copy: TripMutation) {
    if (this.editMode) {
      if (!deepEquals(this.tripMutation.startInfo.location, copy.startInfo.location)) {
        copy.routeGeometry = null;
      }

      if (this.tripMutation.endInfo && copy.endInfo && !deepEquals(this.tripMutation.endInfo.location, copy.endInfo.location)) {
        copy.routeGeometry = null;
      }

      if (!deepEquals(this.tripMutation.stops, copy.stops)) {
        copy.routeGeometry = null;
      }

      this.handleTripInfoInput(copy);
    } else {
      this.$emit('input', copy); // only for view mode and pilot/tag change
    }
  }

  private emitEdit(field: string) {
    this.$emit('toggleEditMode');

    this.$nextTick(() => {
      if (field) {
        if (field === 'note') {
          this.$refs.worknote.focusField();
        } else if (field.startsWith('stop-')) {
          this.$refs.stops.focusField(field);
        } else if (field.startsWith('start-')) {
          this.$refs.start.focusField(field);
        } else if (field.startsWith('end-')) {
          this.$refs.end.focusField(field);
        }
      }
    });
  }

  private handleSave() {
    this.$emit('save');
  }

  private handleNewTripCurrentDateTimeAdjustment(trip: TripMutation) {
    const startTimestamp = new Date(trip.startInfo.timestamp)
    const endTimestamp = new Date(trip.endInfo.timestamp)
    endTimestamp.setMinutes(endTimestamp.getMinutes() - 60)
    const validStartTimestamp = Math.max(endTimestamp.getTime(), startTimestamp.getTime())

    trip.startInfo = { ...this.tripMutation.startInfo, timestamp: new Date(validStartTimestamp).toISOString() }

    this.handleInput(trip);
  }

  private handleStartInfoValueChanged(trip: TripMutation) {
    this.shiftEndOdometerBackwards = false;
    if (this.tripMutation.endInfo && this.tripMutation.endInfo.timestamp && trip.startInfo.timestamp && trip.startInfo.timestamp !== this.tripMutation.startInfo.timestamp && this.duration && this.distance) {
      const startTimestamp = new Date(trip.startInfo.timestamp)
      const endTimestamp = new Date(this.tripMutation.endInfo.timestamp)

      if (startTimestamp > endTimestamp) {
        endTimestamp.setTime(startTimestamp.getTime())
        endTimestamp.setMinutes(endTimestamp.getMinutes() + this.duration)
        const newEndTimestamp = this.maxTime ? Math.min(endTimestamp.getTime(), new Date(this.maxTime).getTime()) : endTimestamp

        trip.endInfo = { ...this.tripMutation.endInfo, timestamp: new Date(newEndTimestamp).toISOString() }
      } else {
        // Math is:  start + dist/minSpeed > endTimestamp > start + dist/maxSpeed

        const maxEndTimestampForValidSpeed = new Date(startTimestamp)
        maxEndTimestampForValidSpeed.setMinutes(startTimestamp.getMinutes() + (this.distance / 1000 / minSpeed) * 60)

        const maxEndTimestampForValidDuration = new Date(startTimestamp)
        maxEndTimestampForValidDuration.setMinutes(startTimestamp.getMinutes() + maxDuration)

        const validMaxEndTimestamp = Math.min(endTimestamp.getTime(), maxEndTimestampForValidSpeed.getTime(), maxEndTimestampForValidDuration.getTime())

        const minEndTimestampForValidSpeed = new Date(startTimestamp)
        minEndTimestampForValidSpeed.setMinutes(startTimestamp.getMinutes() + (this.distance / 1000 / maxSpeed) * 60)

        const minEndTimestampForValidDuration = new Date(startTimestamp)
        minEndTimestampForValidDuration.setMinutes(startTimestamp.getMinutes() + minDuration)

        const validEndTimestamp = Math.max(validMaxEndTimestamp, minEndTimestampForValidSpeed.getTime(), minEndTimestampForValidDuration.getTime())

        const currentTime = new Date();
        if (new Date(validEndTimestamp) > currentTime) {
          const startTime = new Date(currentTime);
          startTime.setMinutes(currentTime.getMinutes() - this.distance / 1000 / maxSpeed * 60);

          trip.endInfo = { ...this.tripMutation.endInfo, timestamp: currentTime.toISOString() }
          trip.startInfo = { ...this.tripMutation.startInfo, timestamp: new Date(startTime).toISOString() }
        } else {
          trip.endInfo = { ...this.tripMutation.endInfo, timestamp: new Date(validEndTimestamp).toISOString() }
        }
      }
    }

    if (this.tripMutation.endInfo && this.tripMutation.endInfo.odometer && trip.startInfo.odometer && trip.startInfo.odometer !== this.tripMutation.startInfo.odometer && this.duration && this.distance) {
      const startOdometer = trip.startInfo.odometer;
      const endOdometer = this.tripMutation.endInfo.odometer;

      if (startOdometer > endOdometer) {
        const newEndOdometer = this.maxOdometer ? Math.min(startOdometer + this.distance, this.maxOdometer) : startOdometer + this.distance;
        trip.endInfo = { ...this.tripMutation.endInfo, odometer: newEndOdometer }
      } else {
        // Math is start + minSpeed * duration < endOdo < start + maxSpeed * duration

        const maxEndOdometerForValidSpeed = startOdometer + maxSpeed * (this.duration / 60) * 1000
        const validMaxOdometer = Math.min(endOdometer, maxEndOdometerForValidSpeed)

        const minEndOdometerForValidSpeed = startOdometer + minSpeed * (this.duration / 60) * 1000
        const validOdometer = Math.max(validMaxOdometer, minEndOdometerForValidSpeed)

        trip.endInfo = { ...this.tripMutation.endInfo, odometer: validOdometer }
      }
    }

    this.handleInput(trip);
  }

  private handleEndInfoValueChanged(trip: TripMutation) {
    if (this.tripMutation.startInfo && this.tripMutation.startInfo.timestamp && trip.endInfo.timestamp && trip.endInfo.timestamp !== this.tripMutation.endInfo.timestamp && this.duration && this.distance) {
      const startTimestamp = new Date(this.tripMutation.startInfo.timestamp)
      const endTimestamp = new Date(trip.endInfo.timestamp)

      if (startTimestamp > endTimestamp) {
        startTimestamp.setTime(endTimestamp.getTime())
        startTimestamp.setMinutes(startTimestamp.getMinutes() - this.duration)
        const newStartTimestamp = this.minTime ? Math.max(startTimestamp.getTime(), new Date(this.minTime).getTime()) : startTimestamp.getTime()

        trip.startInfo = { ...this.tripMutation.startInfo, timestamp: new Date(newStartTimestamp).toISOString() }
      } else {
        // Math is:  end - dist/minSpeed < startTimestamp < end - dist/maxSpeed

        const maxStartTimestampForValidSpeed = new Date(endTimestamp)
        maxStartTimestampForValidSpeed.setMinutes(endTimestamp.getMinutes() - (this.distance / 1000 / maxSpeed) * 60)

        const maxStartTimestampForValidDuration = new Date(endTimestamp)
        maxStartTimestampForValidDuration.setMinutes(endTimestamp.getMinutes() - minDuration)

        const validMaxStartTimestamp = Math.min(startTimestamp.getTime(), maxStartTimestampForValidSpeed.getTime(), maxStartTimestampForValidDuration.getTime())

        const minStartTimestampForValidSpeed = new Date(endTimestamp)
        minStartTimestampForValidSpeed.setMinutes(endTimestamp.getMinutes() - (this.distance / 1000 / minSpeed) * 60)

        const minStartTimestampForValidDuration = new Date(endTimestamp)
        minStartTimestampForValidDuration.setMinutes(endTimestamp.getMinutes() - maxDuration)

        const validStartTimestamp = Math.max(validMaxStartTimestamp, minStartTimestampForValidSpeed.getTime(), minStartTimestampForValidDuration.getTime())

        trip.startInfo = { ...this.tripMutation.startInfo, timestamp: new Date(validStartTimestamp).toISOString() }
      }
    }

    if (this.tripMutation.startInfo && this.tripMutation.startInfo.odometer && trip.endInfo.odometer && trip.endInfo.odometer !== this.tripMutation.endInfo.odometer && this.duration && this.distance) {
      const startOdometer = this.tripMutation.startInfo.odometer
      const endOdometer = trip.endInfo.odometer

      if (startOdometer > endOdometer) {
        const newStartOdometer = this.minOdometer !== undefined ? Math.max(endOdometer - this.distance, this.minOdometer) : endOdometer - this.distance;
        trip.startInfo = { ...this.tripMutation.startInfo, odometer: newStartOdometer }
      } else {
        // Math is end - minSpeed * duration > startOdo > end - maxSpeed * duration

        const maxStartOdometerForValidSpeed = endOdometer - minSpeed * (this.duration / 60) * 1000
        const validMaxOdometer = Math.min(startOdometer, maxStartOdometerForValidSpeed)

        const minStartOdometerForValidSpeed = endOdometer - maxSpeed * (this.duration / 60) * 1000
        const validOdometer = Math.max(validMaxOdometer, minStartOdometerForValidSpeed)

        trip.startInfo = { ...this.tripMutation.startInfo, odometer: validOdometer }
      }
    }

    this.handleInput(trip);
  }

  private async handleTripInfoInput(newTrip: TripMutation) {
    const newStartMoniker = newTrip.startInfo.address && newTrip.startInfo.address.moniker
    const oldStartMoniker = this.tripMutation.startInfo.address && this.tripMutation.startInfo.address.moniker
    const startMonikerChanged = newStartMoniker && newStartMoniker !== oldStartMoniker
    if (startMonikerChanged) {
      this.applyRule(newTrip, this.startRules, newTrip.startInfo.location)
    }

    const newEndMoniker = newTrip.endInfo.address && newTrip.endInfo.address.moniker
    const oldEndMoniker = this.tripMutation.endInfo.address && this.tripMutation.endInfo.address.moniker
    const endMonikerChanged = newEndMoniker && newEndMoniker !== oldEndMoniker
    if (endMonikerChanged) {
      this.applyRule(newTrip, this.endRules, newTrip.endInfo.location)
    }

    newTrip.stops = newTrip.stops || []
    const tripStopsChanged = !deepEquals(newTrip.stops, this.tripMutation.stops)
    if (newStartMoniker && newEndMoniker && (startMonikerChanged || endMonikerChanged || tripStopsChanged)) {
      await this.fetchAndUpdateDirection(newTrip)
    }

    this.$emit('input', newTrip);
  }

  private updateDirection(trip: TripMutation, directionData: DirectionData) {
    this.latestDirectionData = directionData
    trip.routeGeometry = directionData.routeGeometry

    if (this.shiftEndOdometerBackwards) {
      const shiftedOdo = trip.startInfo.odometer + Math.round(directionData.distance * 1000);
      trip.endInfo.odometer = this.maxOdometer ? Math.min(shiftedOdo, this.maxOdometer) : shiftedOdo

      const shiftedDate = new Date(trip.endInfo.timestamp)
      shiftedDate.setSeconds(shiftedDate.getSeconds() - directionData.duration)
      const newStartTimestamp = this.minTime ? Math.max(shiftedDate.getTime(), new Date(this.minTime).getTime()) : shiftedDate.getTime()
      trip.startInfo.timestamp = new Date(newStartTimestamp).toISOString()
    } else if ((this.isStartInfoPrefilled && !this.isEndInfoPrefilled) || !this.addMode) {
      const shiftedOdo = trip.startInfo.odometer + Math.round(directionData.distance * 1000);
      trip.endInfo.odometer = this.maxOdometer ? Math.min(shiftedOdo, this.maxOdometer) : shiftedOdo

      const shiftedDate = new Date(trip.startInfo.timestamp)
      shiftedDate.setSeconds(shiftedDate.getSeconds() + directionData.duration)
      const newEndTimestamp = this.maxTime ? Math.min(shiftedDate.getTime(), new Date(this.maxTime).getTime()) : shiftedDate.getTime()
      trip.endInfo.timestamp = new Date(newEndTimestamp).toISOString()
    } else if (this.isEndInfoPrefilled && !this.isStartInfoPrefilled) {
      const shiftedOdo = trip.endInfo.odometer - Math.round(directionData.distance * 1000);
      trip.startInfo.odometer = this.minOdometer !== undefined ? Math.max(shiftedOdo, this.minOdometer) : shiftedOdo

      const shiftedDate = new Date(trip.endInfo.timestamp)
      shiftedDate.setSeconds(shiftedDate.getSeconds() - directionData.duration)
      const newStartTimestamp = this.minTime ? Math.max(shiftedDate.getTime(), new Date(this.minTime).getTime()) : shiftedDate.getTime()
      trip.startInfo.timestamp = new Date(newStartTimestamp).toISOString()
    } else if (this.isFirstTrip) {
      if (trip.startInfo.odometer !== undefined) {
        trip.endInfo.odometer = trip.startInfo.odometer + Math.round(directionData.distance * 1000);
      }

      if (trip.endInfo.timestamp) {
        const shiftedDate = new Date(trip.endInfo.timestamp)
        shiftedDate.setSeconds(shiftedDate.getSeconds() - directionData.duration)
        trip.startInfo.timestamp = new Date(shiftedDate.getTime()).toISOString()
      }
    } else if (this.isFillingGap) {
      const shiftedOdo = trip.startInfo.odometer + Math.round(directionData.distance * 1000);
      trip.endInfo.odometer = this.maxOdometer ? Math.min(shiftedOdo, this.maxOdometer) : shiftedOdo

      if (trip.startInfo.timestamp) {
        const shiftedDate = new Date(trip.startInfo.timestamp)
        shiftedDate.setSeconds(shiftedDate.getSeconds() + directionData.duration)
        const newEndTimestamp = this.maxTime ? Math.min(shiftedDate.getTime(), new Date(this.maxTime).getTime()) : shiftedDate.getTime()
        trip.endInfo.timestamp = new Date(newEndTimestamp).toISOString()
      }
    }
  }

  private async fetchAndUpdateDirection(trip: TripMutation) {
    let directionData = this.getDirectionFromDirectedRoutes(trip)

    if (!directionData) {
      directionData = await this.getDirectionFromMapbox(trip)
    }

    if (directionData) {
      this.updateDirection(trip, directionData)
    }
  }

  private getDirectionFromDirectedRoutes(trip: TripMutation): DirectionData {
    if (trip.stops.length > 0 || this.directedRoutesStatus !== LoadState.Success) {
      return null
    }

    const fromZone = findClosestZoneContainingLocation(this.zones, trip.startInfo.location)
    const toZone = findClosestZoneContainingLocation(this.zones, trip.endInfo.location)

    if (!fromZone || !toZone) {
      return null
    }

    const matchingRoute = this.directedRoutesResponse
      .find(dr => dr.from.id === fromZone.id && dr.to.id === toZone.id)

    if (!matchingRoute) {
      return null
    }

    return {
      distance: matchingRoute.distance / 1000.0,
      duration: matchingRoute.duration / 1000.0,
      routeGeometry: matchingRoute.routeGeometry
    }
  }

  private async getDirectionFromMapbox(trip: TripMutation): Promise<DirectionData> {
    const locations = [
      trip.startInfo.location,
      ...trip.stops.filter(s => s).map(s => s.location),
      trip.endInfo.location
    ]

    try {
      const direction: DirectionResponse = await this.fetchDirection({ locations })

      if (direction.routes && direction.routes[0]) {
        const [route] = direction.routes

        const latLngsString = route.geometry.coordinates.map(([lon, lat]) => `${lon} ${lat}`).join(',')

        return {
          distance: route.distance / 1000.0,
          duration: route.duration,
          routeGeometry: `LINESTRING( ${latLngsString} )`
        }
      }

      return null
    } catch (error) {
      return null
    }
  }

  private applyRule(trip: TripMutation, rules: RuleWithZones[], location: Location) {
    const rulesZones = [].concat(...rules.map(r => r.zones))
    const matchingZone = findClosestZoneContainingLocation(rulesZones, location)
    if (matchingZone) {
      const matchingRule = rules.find(r => r.zones.map(z => z.id).includes(matchingZone.id))

      if (!trip.pilotId && matchingRule.pilotId) {
        trip.pilotId = parseInt(matchingRule.pilotId)
      }

      if (!trip.tagId && matchingRule.tagId) {
        trip.tagId = parseInt(matchingRule.tagId)
      }

      if (!trip.work && matchingRule.work) {
        trip.work = matchingRule.work;
      }

      if (!trip.note && matchingRule.note) {
        trip.note = matchingRule.note;
      }
    }
  }
}
